/**
 * Right-sizing hints for uploaded images.
 *
 * WHY THIS EXISTS
 * ---------------
 * Round 2 made every upload serve as WebP, which fixed the *format*. The next
 * mobile report showed the remaining 204 KiB under "improve image delivery" was
 * not a format problem at all — it was five images that are simply far bigger
 * than the box they are drawn in:
 *
 *   1920x293   ad creative     drawn at  665x101    97.1 KiB recoverable
 *   1080x1080  broker logo     drawn at   70x70     37.7 KiB
 *   1330x1198  broker logo     drawn at  112x101    31.7 KiB
 *   1080x250   site logo       drawn at  217x50     23.1 KiB
 *   1001x1000  broker logo     drawn at  112x112     8.4 KiB
 *
 * Re-encoding those to WebP could not help; the dimensions were the problem.
 * A server cannot infer a display size from a filename, so the component that
 * knows the box declares it: appending `?w=<css px>` makes the server write a
 * derivative at that width (`webpVariantPath` in
 * `server/services/imageOptimize.ts`) and cache it beside the others.
 *
 * Rule of thumb: pass roughly 2x the CSS size, so the image stays sharp on a
 * 2x screen rather than being visibly soft.
 *
 * WHY IT IS SAFE
 * --------------
 * Purely additive. A URL with no hint behaves exactly as it does today; the
 * server never upscales; and if the image pipeline is unavailable (no `sharp`)
 * the request falls through to the untouched original file. Nothing here can
 * break an image that works now.
 */
const UPLOAD_IMAGE_PREFIX = "/uploads/images/";

/**
 * Append a width hint to one of our own uploaded images.
 *
 * Ignored for anything not under `/uploads/images/` — `/img/*` and `/assets/*`
 * are already right-sized at build time, and third-party URLs (news feeds) are
 * not ours to rewrite.
 */
export function sizedUploadUrl(url: string | null | undefined, width: number): string {
  const raw = String(url || "").trim();
  if (!raw.includes(UPLOAD_IMAGE_PREFIX)) return raw;
  // A caller that already chose a width knows better than we do.
  if (/[?&]w=/.test(raw)) return raw;
  if (!Number.isFinite(width) || width <= 0) return raw;

  const target = Math.max(16, Math.min(4096, Math.round(width)));
  return `${raw}${raw.includes("?") ? "&" : "?"}w=${target}`;
}

/**
 * Logo boxes across the site top out around 112 CSS px (BrokerCard 80,
 * broker-detail 112, cards 48–64). 256 covers 128 CSS px at 2x, so there is
 * headroom if a page ever draws one larger.
 */
export const LOGO_IMAGE_WIDTH = 256;

/** The header logo is drawn up to 206 CSS px wide at the `xl` breakpoint. */
export const SITE_LOGO_IMAGE_WIDTH = 512;

/** Below this an ad creative would be visibly soft whatever the slot says. */
const AD_CREATIVE_MIN_WIDTH = 320;

/**
 * Width to request for an ad creative, from the slot's declared width
 * (Admin → Ads → Slots). Floored so a missing or wrong declaration can never
 * produce a uselessly small creative.
 */
export function adCreativeWidth(declaredWidth: number): number {
  if (!Number.isFinite(declaredWidth) || declaredWidth <= 0) return AD_CREATIVE_MIN_WIDTH;
  return Math.max(AD_CREATIVE_MIN_WIDTH, Math.round(declaredWidth * 2));
}
